declare const __webpack_init_sharing__: (scope: string) => Promise<void>;
declare const __webpack_share_scopes__: { default: any };

const SHARE_FROM = "bronte-viewer-express-api";

function shareScopeHasAnyVersion(
  pkg: string,
  scope: Record<string, Record<string, unknown>>,
): boolean {
  const versions = scope[pkg];
  return !!(versions && typeof versions === "object" && Object.keys(versions).length > 0);
}

/**
 * ESM build externalizes react/react-dom, so webpack only puts react/jsx-runtime in the
 * default share scope. Remotes then bundle their own React. Register the same modules the
 * app uses (dynamic import) so container.init() exposes host React to remotes.
 */
async function ensureHostReactInDefaultShareScope(): Promise<void> {
  const scope = __webpack_share_scopes__?.default;
  if (!scope || typeof scope !== "object") return;

  const [reactNs, reactDomNs] = await Promise.all([import("react"), import("react-dom")]);
  const ReactMod = (reactNs as { default?: unknown }).default ?? reactNs;
  const ReactDOMMod = (reactDomNs as { default?: unknown }).default ?? reactDomNs;
  const version =
    ReactMod &&
    typeof ReactMod === "object" &&
    typeof (ReactMod as { version?: string }).version === "string"
      ? (ReactMod as { version: string }).version
      : "18.2.0";

  const makeSlot = (mod: unknown) => ({
    get: () => () => mod,
    from: SHARE_FROM,
    eager: true,
    loaded: 1 as const,
  });

  if (!shareScopeHasAnyVersion("react", scope)) {
    scope.react = { [version]: makeSlot(ReactMod) };
  }
  if (!shareScopeHasAnyVersion("react-dom", scope)) {
    scope["react-dom"] = { [version]: makeSlot(ReactDOMMod) };
  }
}

/**
 * Dynamically loads a Module Federation remote container and retrieves a module from it.
 * This replaces static `remotes` in webpack.config.js, allowing URLs to be resolved at runtime.
 *
 * @param url - The remote entry URL (e.g. remoteEntry.js)
 * @param scope - The global container name (e.g. "cloudflicks", "TDX")
 * @param module - The exposed module path (e.g. "./SPPPlayer")
 */
export async function loadRemoteModule(url: string, scope: string, module: string): Promise<any> {
  await __webpack_init_sharing__("default");
  await ensureHostReactInDefaultShareScope();

  if (!(window as any)[scope]) {
    await new Promise<void>((resolve, reject) => {
      const script = document.createElement("script");
      script.src = url;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load remote entry: ${url}`));
      document.head.appendChild(script);
    });
  }

  const container = (window as any)[scope];
  await container.init(__webpack_share_scopes__.default);
  const factory = await container.get(module);
  return factory();
}
